import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Table, Modal, Form, Alert } from 'react-bootstrap';
import { getSites, createSite, deleteSite } from '../services/api';
import axios from 'axios';

function SiteList({ tenancyEnabled = false }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSite, setNewSite] = useState({
    name: '',
    site_path: '',
    brand: 'previews',
    customer_id: 'internal'
  });
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    if (tenancyEnabled) axios.get('/api/management/customers/').then(({ data }) => setCustomers(data))
      .catch(() => setMessage({ text: 'Could not load customers', type: 'danger' }));
  }, [tenancyEnabled]);

  const changeOwner = async (site, customerId) => {
    const customer = customers.find(item => item.id === customerId);
    if (!window.confirm(`Give ${customer?.name || customerId} access to ${site.name}? The site address will stay the same.`)) return;
    try {
      await axios.post(`/api/management/sites/${encodeURIComponent(site.site_id)}/owner/`, { customer_id: customerId });
      await fetchSites();
      setMessage({ text: 'Site ownership updated', type: 'success' });
    } catch (err) {
      setMessage({ text: typeof err.response?.data === 'string' ? err.response.data : 'Could not change ownership', type: 'danger' });
    }
  };

  const fetchSites = async () => {
    try {
      setLoading(true);
      const data = await getSites();
      setSites(data);
      setError(null);
    } catch (err) {
      setError('Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSite = async (e) => {
    e.preventDefault();
    try {
      await createSite(newSite);
      setShowCreateModal(false);
      setMessage({ text: 'Site created successfully!', type: 'success' });
      fetchSites();
    } catch (err) {
      setMessage({ text: 'Failed to create site', type: 'danger' });
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (window.confirm(`Are you sure you want to delete this site?`)) {
      try {
        await deleteSite(siteId);
        setMessage({ text: 'Site deleted successfully!', type: 'success' });
        fetchSites();
      } catch (err) {
        setMessage({ text: 'Failed to delete site', type: 'danger' });
      }
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Sites</h1>
        <Button 
          className="me-2" 
          variant="primary" 
          size="sm"
          onClick={() => setShowCreateModal(true)}
        >
          Create Site
        </Button>
      </div>

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {tenancyEnabled && <Form.Group className="mb-3" controlId="customerFilter">
        <Form.Label>Customer</Form.Label>
        <Form.Select value={customerFilter} onChange={e => setCustomerFilter(e.target.value)}>
          <option value="all">All customers</option>
          {customers.map(customer => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
          <option value="unassigned">Unassigned</option>
        </Form.Select>
      </Form.Group>}

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Name</th>
            {tenancyEnabled && <th>Owner</th>}
            <th>Site Path</th>
            <th>Certificate Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sites.filter(site => customerFilter === 'all' || (customerFilter === 'unassigned' ? !site.customer_id : site.customer_id === customerFilter)).map((site) => (
            <tr key={site.site_id}>
              <td>
                <Link to={`/sites/${site.site_id}`}>{site.name}</Link>
              </td>
              {tenancyEnabled && <td>
                <Form.Select size="sm" aria-label={`Owner of ${site.name}`} value={site.customer_id || ''}
                  onChange={e => changeOwner(site, e.target.value)} disabled={!site.customer_id}>
                  {!site.customer_id && <option value="">Unassigned / internal</option>}
                  {customers.map(customer => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
                </Form.Select>
                {site.management_status !== 'active' && <small className="text-muted">{site.management_status}</small>}
              </td>}
              <td>{site.site_path}</td>
              <td>
                {site.has_valid_cert ? (
                  <span className="text-success">Valid</span>
                ) : (
                  <span className="text-muted">Not configured</span>
                )}
              </td>
              <td>
                <Button 
                  as={Link} 
                  to={`/sites/${site.site_id}`} 
                  variant="info" 
                  size="sm"
                  className="me-2"
                >
                  Manage
                </Button>
                <Button 
                  variant="danger" 
                  size="sm" 
                  onClick={() => handleDeleteSite(site.site_id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Create Site Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Site</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateSite}>
            {tenancyEnabled && <Form.Group className="mb-3" controlId="newSiteOwner">
              <Form.Label>Owner</Form.Label>
              <Form.Select value={newSite.customer_id} onChange={e => {
                const owner = customers.find(customer => customer.id === e.target.value);
                setNewSite({ ...newSite, customer_id: e.target.value,
                  brand: owner?.brands.length === 1 ? owner.brands[0] : newSite.brand });
              }}>
                {customers.map(customer => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
              </Form.Select>
              <Form.Text>The owner determines customer access. The preview domain determines the address.</Form.Text>
            </Form.Group>}
            <Form.Group className="mb-3">
              <Form.Label>Site Name</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="My Awesome Site" 
                value={newSite.name}
                onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                required
              />
              <Form.Text className="text-muted">
                This name will be used to identify your site and generate an internal URL.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Brand</Form.Label>
              <Form.Select
                value={newSite.brand}
                onChange={(e) => setNewSite({ ...newSite, brand: e.target.value })}
              >
                <option value="previews">Previews</option>
                <option value="yds">YDS (dev.yds.agency)</option>
                <option value="mogged_nz">Mogged</option>
                <option value="mogged_au">Mogged AU</option>
                <option value="buynz_digital">BuyNZ Digital</option>
              </Form.Select>
              <Form.Text className="text-muted">
                This determines the internal domain structure.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Site Path (optional)</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="Leave empty for auto-generation" 
                value={newSite.site_path}
                onChange={(e) => setNewSite({ ...newSite, site_path: e.target.value })}
              />
              <Form.Text className="text-muted">
                If left empty, a path will be generated based on the site name. This will be used for file storage.
              </Form.Text>
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Create Site
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default SiteList;
